import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { AssetImage } from './AssetImage';
import { useSlideTrack } from '../hooks/useSlideTrack';
import type { ManualPage } from '../data/collection';
interface ManualViewerProps {
  pages: ManualPage[];
  modelName: string;
  volume: string;
  onOpen: (index: number) => void;
}
const arrowClass = 'flex h-12 w-12 items-center justify-center rounded-full bg-paper text-graphite ring-1 ring-inset ring-graphite/15 transition-[box-shadow,color] duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-35 enabled:hover:text-coral-ink enabled:hover:ring-coral/50 focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/40';

/**
 * Page-by-page reader for one model: large swipeable page, arrows, counter,
 * thumbnails and tap-to-enlarge.
 */
export function ManualViewer({
  pages,
  modelName,
  volume,
  onOpen
}: ManualViewerProps) {
  const {
    trackRef,
    active,
    goTo
  } = useSlideTrack(pages.length);
  const total = pages.length;
  const onKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(active - 1);
    }
  };
  return <div className="grid min-w-0 grid-cols-1 items-start gap-6 sm:gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:gap-14 [&>*]:min-w-0" data-id="element-120" data-mp="2me6l7hbzzkzl">
      <div className="mx-auto w-full min-w-0 max-w-[min(520px,100%)]" data-id="element-121" data-mp="3f5gefdnjurvw">
        <div className="relative w-full overflow-hidden rounded-[8px] bg-paper shadow-[0_24px_48px_-28px_rgba(52,52,49,0.55)] ring-1 ring-graphite/10" data-id="element-122" data-mp="3155qojhjdtey">
          <ul ref={trackRef} tabIndex={0} onKeyDown={onKeyDown} aria-label={`Páginas del manual: ${modelName}`} className="no-scrollbar flex w-full min-w-0 max-w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-coral/30" data-id="element-123" data-mp="355fsdjyapxo0">
            {pages.map((page, index) => <li key={page.file} aria-label={`Página ${index + 1} de ${total}`} className="w-full min-w-full max-w-full shrink-0 basis-full snap-center" data-id="element-124" data-mp="3rhiljat3wynj" data-mpi={index}>
                <button type="button" onClick={() => onOpen(index)} aria-label={`Ampliar página ${index + 1}: ${page.caption}`} style={{
              aspectRatio: `${page.width} / ${page.height}`
            }} className="block w-full cursor-zoom-in focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-coral/40" data-id="element-125" data-mp="3hol8vlasq3wu">
                  <AssetImage image={page} data-id="element-126" data-mp="2ysle62qfom9k" />
                </button>
              </li>)}
          </ul>
          <span className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-paper/95 px-3 py-1.5 text-[0.8rem] font-bold text-graphite shadow-sm ring-1 ring-graphite/10" data-id="element-127" data-mp="11vundolxml1a">
            <ZoomInIcon className="h-3.5 w-3.5" aria-hidden data-id="element-128" data-mp="19hq1l9py2l4v" />
            Tocá para ampliar
          </span>
        </div>
      </div>

      <div className="lg:pt-4" data-id="element-129" data-mp="1e9hkcy2uzmkg">
        <p className="text-[0.95rem] font-bold text-sage-ink" data-id="element-130" data-mp="1e983q13perrf">{volume}</p>
        <h3 className="mt-1 font-serif text-[1.6rem] font-semibold leading-tight text-graphite sm:text-[1.9rem]" data-id="element-131" data-mp="1j680flenyi5v">
          {modelName}
        </h3>

        <div className="mt-6 flex items-center justify-between gap-4 border-y border-ivory-line py-4" data-id="element-132" data-mp="qivpy9ls20sx">
          <div aria-live="polite" className="min-w-0" data-id="element-133" data-mp="vhykh044somn">
            <p className="text-[0.88rem] font-bold tabular-nums text-coral-ink" data-id="element-134" data-mp="vgdpxahth5os">
              Página {active + 1} de {total}
            </p>
            <p className="truncate text-[1.05rem] font-semibold text-graphite" data-id="element-135" data-mp="26x3zn3lg8fn5">{pages[active].caption}</p>
          </div>
          <div className="flex shrink-0 gap-2" data-id="element-136" data-mp="2bvoxwxh54p8y">
            <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Página anterior" className={arrowClass} data-id="element-137" data-mp="2budp2j8u8raz">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-138" data-mp="3fuln93qcafah" />
            </button>
            <button type="button" onClick={() => goTo(active + 1)} disabled={active === total - 1} aria-label="Página siguiente" className={arrowClass} data-id="element-139" data-mp="3ttldgfti328r">
              <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-140" data-mp="7lliqkfkttuw" />
            </button>
          </div>
        </div>

        <ul aria-label="Miniaturas de las páginas" className="no-scrollbar mt-5 flex w-full min-w-0 max-w-full gap-2.5 overflow-x-auto p-1 lg:grid lg:grid-cols-3 lg:gap-3 lg:overflow-visible lg:p-0" data-id="element-141" data-mp="2n0px62nmcgh">
          {pages.map((page, index) => {
          const selected = index === active;
          return <li key={page.file} className="w-[64px] shrink-0 sm:w-[76px] lg:w-auto" data-id="element-142" data-mp="qdecih2gzj3o" data-mpi={index}>
                <button type="button" onClick={() => goTo(index)} aria-label={`Ir a la página ${index + 1}: ${page.caption}`} aria-current={selected ? 'true' : undefined} className={`block w-full overflow-hidden rounded-[4px] bg-paper ring-inset transition-[box-shadow,opacity] duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/50 ${selected ? 'ring-[3px] ring-coral' : 'opacity-75 ring-1 ring-graphite/15 hover:opacity-100'}`} data-id="element-143" data-mp="lfl5xmx98n2v">
                  <span className="block" style={{
                aspectRatio: `${page.width} / ${page.height}`
              }} data-id="element-144" data-mp="r255u0sk165n">
                    <AssetImage image={page} compact thumb data-id="element-145" data-mp="c91p1vmsewfz" />
                  </span>
                </button>
                <span className="mt-1 hidden text-[0.78rem] font-semibold leading-tight text-graphite-soft lg:block" data-id="element-146" data-mp="h7wevhj69l9l">
                  {index + 1}. {page.caption}
                </span>
              </li>;
        })}
        </ul>
      </div>
    </div>;
}